// src/services/firebase.js
import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithCredential,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  serverTimestamp,
  getDocs,
} from "firebase/firestore";

// Configuração lida das variáveis de ambiente (.env)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// ── Auth helpers ───────────────────────────────────────────────────────────────

async function salvarUsuarioFirestore(user, extra = {}) {
  const userRef = doc(db, "users", user.uid);
  const snap = await getDoc(userRef);
  
  if (!snap.exists()) {
    // VULN-009: Role definida pelo Custom Claim do backend (set_admin.js), não por email hardcoded
    const tokenResult = await user.getIdTokenResult();
    const role = tokenResult.claims.admin ? "organizer" : "participant";
    await setDoc(userRef, {
      nome: user.displayName || extra.nome || "Usuário",
      email: user.email,
      cpf: extra.cpf || "",
      telefone: extra.telefone || "",
      role: role,
      criadoEm: serverTimestamp(),
    });
  } else {
    // Atualiza role se Custom Claim admin foi adicionado mas role ainda é participant
    const tokenResult = await user.getIdTokenResult();
    if (tokenResult.claims.admin && snap.data().role !== "organizer") {
      await setDoc(userRef, { role: "organizer" }, { merge: true });
    }
  }
  return user;
}

export async function cadastrar({ nome, email, senha, cpf, telefone }) {
  const cred = await createUserWithEmailAndPassword(auth, email, senha);
  await updateProfile(cred.user, { displayName: nome });
  await salvarUsuarioFirestore(cred.user, { nome, cpf, telefone });
  return cred.user;
}

export async function entrar({ email, senha }) {
  const cred = await signInWithEmailAndPassword(auth, email, senha);
  return cred.user;
}

export async function entrarComGoogle() {
  const provider = new GoogleAuthProvider();
  const cred = await signInWithPopup(auth, provider);
  await salvarUsuarioFirestore(cred.user);
  return cred.user;
}

export async function vincularGoogleComSenha(senha, error) {
  const pendingCred = GoogleAuthProvider.credentialFromError(error);
  const email = error.customData.email;
  // Faz o login tradicional com email e senha
  const userCred = await signInWithEmailAndPassword(auth, email, senha);
  // Vincula a credencial do Google a esta conta autenticada
  const linkedCred = await linkWithCredential(userCred.user, pendingCred);
  await salvarUsuarioFirestore(linkedCred.user);
  return linkedCred.user;
}

export async function sair() {
  await signOut(auth);
}

export async function recuperarSenha(email) {
  await sendPasswordResetEmail(auth, email);
}

export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

// ── Firestore helpers ──────────────────────────────────────────────────────────

export async function buscarUsuario(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function buscarEventos() {
  const snap = await getDocs(
    query(collection(db, "events"), orderBy("createdAt", "desc"))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function buscarEvento(id) {
  const snap = await getDoc(doc(db, "events", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Escuta ingressos do usuário em tempo real */
export function escutarIngressos(uid, callback) {
  const q = query(
    collection(db, "tickets"),
    where("userId", "==", uid)
  );
  return onSnapshot(q, (snap) => {
    const tickets = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    tickets.sort((a, b) => new Date(b.criadoEm || 0) - new Date(a.criadoEm || 0));
    callback(tickets);
  }, (err) => {
    console.error("Erro em escutarIngressos:", err);
    callback([]);
  });
}

/** Escuta um ingresso específico em tempo real (para atualizar status após webhook) */
export function escutarIngresso(ingressoId, callback) {
  return onSnapshot(doc(db, "tickets", ingressoId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  }, (err) => {
    console.error("Erro em escutarIngresso:", err);
  });
}

/** Escuta eventos em tempo real */
export function escutarEventos(callback) {
  const q = query(collection(db, "events"), orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    const eventos = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(eventos);
  }, (err) => {
    console.error("Erro em escutarEventos:", err);
  });
}

/**
 * @deprecated VULN-013: Use buscarParticipantesEvento() do api.js em vez disso.
 * Escutar participantes via Firestore direto expõe dados de outros organizadores.
 */
export function escutarParticipantesEvento(eventoId, callback) {
  console.warn("[DEPRECATED] escutarParticipantesEvento: Use a API REST /eventos/:id/participantes");
  callback([]);
  return () => {}; // Retorna unsub noop
}


